import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Typography,
  Space,
  message,
  Alert,
  Popconfirm,
  DatePicker,
  Row,
  Col,
  Tabs,
  Grid,
  ConfigProvider,
  List,
  Spin,
  Image,
  Select,
  Empty,
  AutoComplete
} from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ClearOutlined, FilterOutlined, LinkOutlined, FolderOutlined, MobileOutlined, AppstoreOutlined, GlobalOutlined, CopyOutlined } from '@ant-design/icons';
import { applicationAPI, policyAPI } from '../utils/api';
import { Application, AppVersion, Policy, CreatePolicyRequest, UpdatePolicyRequest } from '../types';
import dayjs from 'dayjs';
import JSON5 from 'json5';
import StrategyVisualEditor from '../components/StrategyVisualEditor';


const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

// 策略使用情况类型
interface PolicyUsage {
  categories: { id: number; name: string }[];
  devices: { id: number; deviceCode: string; description?: string }[];
}

const Policies: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [filteredPolicies, setFilteredPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [cloneSubmitting, setCloneSubmitting] = useState(false);
  const [clonePolicy, setClonePolicy] = useState<Policy | null>(null);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [cloneForm] = Form.useForm();
  
  // 策略数据状态（用于可视化编辑器）
  const [strategyData, setStrategyData] = useState<any>(null);
  const [editMode, setEditMode] = useState<'visual' | 'json'>('visual');

  // 应用管理弹窗状态
  const [appManagerVisible, setAppManagerVisible] = useState(false);
  const [appManagerLoading, setAppManagerLoading] = useState(false);
  const [appManagerSaving, setAppManagerSaving] = useState(false);
  const [appManagerPolicy, setAppManagerPolicy] = useState<Policy | null>(null);
  const [appManagerData, setAppManagerData] = useState<any>(null);
  const [appManagerApps, setAppManagerApps] = useState<any[]>([]);
  const [appManagerStrategyName, setAppManagerStrategyName] = useState('');
  const [appManagerPolicyIndex, setAppManagerPolicyIndex] = useState<number | null>(null);
  const [appManagerAppControlId, setAppManagerAppControlId] = useState<string | null>(null);
  const [appManagerAppAllowanceId, setAppManagerAppAllowanceId] = useState<string | null>(null);
  const [appManagerWhitelistApps, setAppManagerWhitelistApps] = useState<string[] | null>(null);
  const [appManagerApplications, setAppManagerApplications] = useState<Application[]>([]);
  const [appManagerSelectedAppId, setAppManagerSelectedAppId] = useState<number | null>(null);

  // 网址管理弹窗状态
  const [networkManagerVisible, setNetworkManagerVisible] = useState(false);
  const [networkManagerLoading, setNetworkManagerLoading] = useState(false);
  const [networkManagerSaving, setNetworkManagerSaving] = useState(false);
  const [networkManagerPolicy, setNetworkManagerPolicy] = useState<Policy | null>(null);
  const [networkManagerData, setNetworkManagerData] = useState<any>(null);
  const [networkManagerStrategyName, setNetworkManagerStrategyName] = useState('');
  const [networkManagerPolicyIndex, setNetworkManagerPolicyIndex] = useState<number | null>(null);
  const [networkManagerNetworkId, setNetworkManagerNetworkId] = useState<string | null>(null);
  const [networkManagerConfigs, setNetworkManagerConfigs] = useState<any[]>([]);
  const [networkManagerApplications, setNetworkManagerApplications] = useState<Application[]>([]);

  // 使用情况弹框状态
  const [usageModalVisible, setUsageModalVisible] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [currentUsage, setCurrentUsage] = useState<PolicyUsage | null>(null);
  const [currentPolicyName, setCurrentPolicyName] = useState('');

  // 搜索条件状态
  const [searchFilters, setSearchFilters] = useState({
    id: '',
    name: '',
    description: '',
    value: '',
    dateRange: null as any
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  // 当policies数据变化时，重新应用过滤器
  useEffect(() => {
    applyFilters(searchFilters);
  }, [policies]);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await policyAPI.getPolicies();
      setPolicies(data);
      setFilteredPolicies(data); // 初始化过滤后的数据
    } catch (err: any) {
      setError(err.message || '获取管控措施列表失败');
      console.error('获取管控措施列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 搜索过滤逻辑
  const applyFilters = (filters: typeof searchFilters) => {
    let filtered = [...policies];

    // ID过滤
    if (filters.id) {
      filtered = filtered.filter(policy =>
        policy.id.toString().includes(filters.id)
      );
    }

    // 名称过滤
    if (filters.name) {
      filtered = filtered.filter(policy =>
        policy.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    // 描述过滤
    if (filters.description) {
      filtered = filtered.filter(policy =>
        policy.description.toLowerCase().includes(filters.description.toLowerCase())
      );
    }

    // 配置值过滤
    if (filters.value) {
      filtered = filtered.filter(policy =>
        policy.value.toLowerCase().includes(filters.value.toLowerCase())
      );
    }

    // 日期范围过滤
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter(policy => {
        const createdAt = dayjs(policy.createdAt);
        return createdAt.isAfter(startDate.startOf('day')) &&
               createdAt.isBefore(endDate.endOf('day'));
      });
    }

    setFilteredPolicies(filtered);
  };

  // 处理搜索
  const handleSearch = (values: any) => {
    const filters = {
      id: values.id || '',
      name: values.name || '',
      description: values.description || '',
      value: values.value || '',
      dateRange: values.dateRange || null
    };
    setSearchFilters(filters);
    applyFilters(filters);
  };

  // 重置搜索
  const handleResetSearch = () => {
    const emptyFilters = {
      id: '',
      name: '',
      description: '',
      value: '',
      dateRange: null
    };
    setSearchFilters(emptyFilters);
    searchForm.resetFields();
    setFilteredPolicies(policies);
  };

  // 生成 GUID
  const generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // 移除时间段中的 allDay 标记，避免保存到后端 JSON
  const stripAllDayFlag = (strategy: any) => {
    if (!strategy) return strategy;
    const cloned = JSON.parse(JSON.stringify(strategy));

    const cleansePolicies = (policies: any) => {
      if (!Array.isArray(policies)) return policies;
      return policies.map((policy: any) => {
        if (!policy) return policy;
        const ranges = Array.isArray(policy.appliedTimeRanges)
          ? policy.appliedTimeRanges.map((range: any) => {
              if (!range) return range;
              const { allDay, ...rest } = range;
              return rest;
            })
          : policy.appliedTimeRanges;
        return { ...policy, appliedTimeRanges: ranges || [] };
      });
    };

    if (cloned?.result) {
      cloned.result.policies = cleansePolicies(cloned.result.policies) || cloned.result.policies;
    }
    if (cloned.policies) {
      cloned.policies = cleansePolicies(cloned.policies);
    }

    return cloned;
  };

  const normalizePolicyData = (data: any) => {
    if (!data) return data;
    if (data.result) return stripAllDayFlag(data);
    return stripAllDayFlag({
      result: data,
      targetUrl: null,
      success: true,
      error: null,
      unAuthorizedRequest: false,
      __abp: true
    });
  };

  const parsePolicyValue = (value: string) => {
    const parsed = JSON5.parse(value || '{}');
    return normalizePolicyData(parsed);
  };

  const stringifyPolicyValue = (data: any) => JSON.stringify(normalizePolicyData(data));

  // 默认策略数据结构（带 result 包装）
  const getDefaultStrategyData = () => ({
    result: {
      strategies: {
        queryPolicyInterval: '600',
        appAllowance: [],
        appControl: [],
        deviceConfig: [],
        network: [],
        appHidden: []
      },
      policies: []
    },
    targetUrl: null,
    success: true,
    error: null,
    unAuthorizedRequest: false,
    __abp: true
  });

  const showModal = (policy?: Policy) => {
    setEditingPolicy(policy || null);
    setModalVisible(true);

    // 使用 setTimeout 确保 Modal 完全渲染后再设置表单值
    setTimeout(() => {
      if (policy) {
        form.setFieldsValue({
          name: policy.name,
          description: policy.description,
          value: policy.value,
        });
        
        // 解析策略数据用于可视化编辑
        try {
          setStrategyData(parsePolicyValue(policy.value));
        } catch (e) {
          console.error('解析策略数据失败:', e);
          setStrategyData(getDefaultStrategyData());
        }
      } else {
        form.resetFields();
        setStrategyData(getDefaultStrategyData());
      }
      setEditMode('visual'); // 默认使用可视化编辑模式
    }, 0);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingPolicy(null);
    setStrategyData(null);
    form.resetFields();
  };

  const openCloneModal = (policy: Policy) => {
    setClonePolicy(policy);
    setCloneModalVisible(true);
    setTimeout(() => {
      cloneForm.setFieldsValue({
        name: `${policy.name}-副本`
      });
    }, 0);
  };

  const handleCloneCancel = () => {
    setCloneModalVisible(false);
    setClonePolicy(null);
    cloneForm.resetFields();
  };

  const handleCloneSubmit = async (values: { name: string }) => {
    if (!clonePolicy) return;
    try {
      setCloneSubmitting(true);
      let normalizedValue = clonePolicy.value;
      if (clonePolicy.value) {
        try {
          const parsed = JSON5.parse(clonePolicy.value);
          normalizedValue = stringifyPolicyValue(parsed);
        } catch {
          normalizedValue = clonePolicy.value;
        }
      }
      await policyAPI.createPolicy({
        name: values.name,
        description: clonePolicy.description,
        value: normalizedValue
      });
      message.success('管控措施克隆成功');
      handleCloneCancel();
      await fetchPolicies();
    } catch (err: any) {
      message.error(err.message || '克隆失败');
    } finally {
      setCloneSubmitting(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      // 根据编辑模式处理配置值
      let normalizedValue: string;
      
      if (editMode === 'visual') {
        // 可视化模式：直接使用 strategyData
        if (!strategyData) {
          message.error('策略数据为空');
          setSubmitting(false);
          return;
        }
        normalizedValue = stringifyPolicyValue(strategyData);
      } else {
        // JSON 模式：解析并验证
        try {
          const parsed = JSON5.parse(values.value);
          normalizedValue = stringifyPolicyValue(parsed);
        } catch (e: any) {
          message.error(`配置值不是有效的 JSON/JSON5：${e?.message || e}`);
          setSubmitting(false);
          return;
        }
      }

      const payload = { 
        name: values.name,
        description: values.description,
        value: normalizedValue 
      };

      if (editingPolicy) {
        // 更新策略
        const updateData: UpdatePolicyRequest = {
          id: editingPolicy.id,
          ...payload
        };
        await policyAPI.updatePolicy(updateData);
        message.success('管控措施更新成功');
      } else {
        // 创建策略
        const createData: CreatePolicyRequest = payload;
        await policyAPI.createPolicy(createData);
        message.success('管控措施创建成功');
      }

      setModalVisible(false);
      setEditingPolicy(null);
      form.resetFields();
      await fetchPolicies();
    } catch (err: any) {
      message.error(err.message || '操作失败');
      console.error('操作失败:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getSortedVersions = (versions?: AppVersion[]) => {
    if (!versions) return [];
    return [...versions].sort((a, b) => parseInt(b.versionCode) - parseInt(a.versionCode));
  };

  const getLatestVersion = (app?: Application) => {
    if (!app) return undefined;
    const sorted = getSortedVersions(app.versions);
    return sorted[0];
  };

  const findApplicationForEntry = (entry: any) => {
    if (!entry) return undefined;
    return appManagerApplications.find((app) =>
      (entry.packageName && app.packageName === entry.packageName) ||
      (entry.applicationName && app.applicationName === entry.applicationName)
    );
  };

  const getAppIcon = (entry: any) => {
    if (entry?.applicationIcon) return entry.applicationIcon;
    const matched = findApplicationForEntry(entry);
    return getLatestVersion(matched)?.icon;
  };

  const getVersionSelectValue = (entry: any) => {
    if (!entry?.versionCode) return undefined;
    return `${entry.versionCode}||${entry.installUrl || ''}`;
  };

  const handleManagerVersionChange = (index: number, value: string) => {
    if (value.includes('||')) return;
    const next = [...appManagerApps];
    next[index] = { ...next[index], versionCode: value };
    setAppManagerApps(next);
  };

  const openAppManager = async (policy: Policy) => {
    setAppManagerVisible(true);
    setAppManagerLoading(true);
    setAppManagerPolicy(policy);
    setAppManagerSelectedAppId(null);

    try {
      const data = parsePolicyValue(policy.value);
      setAppManagerData(data);

      const policyItems = data?.result?.policies || [];
      const selectedIndex = policyItems.findIndex((item: any) => item?.strategies?.appControl);
      if (selectedIndex === -1) {
        setAppManagerPolicyIndex(null);
        setAppManagerAppControlId(null);
        setAppManagerAppAllowanceId(null);
        setAppManagerWhitelistApps(null);
        setAppManagerApps([]);
        setAppManagerStrategyName('');
      } else {
        const selectedPolicyItem = policyItems[selectedIndex];
        const appControlId = selectedPolicyItem?.strategies?.appControl || '';
        const appAllowanceId = selectedPolicyItem?.strategies?.appAllowance || '';
        const appControlStrategy = (data?.result?.strategies?.appControl || []).find((item: any) => item.id === appControlId);
        const appAllowanceStrategy = (data?.result?.strategies?.appAllowance || []).find((item: any) => item.id === appAllowanceId);

        if (!appControlStrategy) {
          setAppManagerPolicyIndex(null);
          setAppManagerAppControlId(null);
          setAppManagerApps([]);
          setAppManagerStrategyName('');
        } else {
          setAppManagerPolicyIndex(selectedIndex);
          setAppManagerAppControlId(appControlId || null);
          setAppManagerApps(appControlStrategy?.apps ? JSON.parse(JSON.stringify(appControlStrategy.apps)) : []);
          setAppManagerStrategyName(appControlStrategy?.name || '');
        }
        setAppManagerAppAllowanceId(appAllowanceId || null);
        if (appAllowanceStrategy?.controlType === 'whitelist') {
          setAppManagerWhitelistApps(Array.isArray(appAllowanceStrategy.apps) ? [...appAllowanceStrategy.apps] : []);
        } else {
          setAppManagerWhitelistApps(null);
        }
      }

      const apps = await applicationAPI.getApplications();
      setAppManagerApplications(apps);
    } catch (err) {
      console.error('加载应用管理数据失败:', err);
      message.error('加载应用管理数据失败');
    } finally {
      setAppManagerLoading(false);
    }
  };

  const closeAppManager = () => {
    setAppManagerVisible(false);
    setAppManagerPolicy(null);
    setAppManagerData(null);
    setAppManagerApps([]);
    setAppManagerStrategyName('');
    setAppManagerPolicyIndex(null);
    setAppManagerAppControlId(null);
    setAppManagerAppAllowanceId(null);
    setAppManagerWhitelistApps(null);
    setAppManagerSelectedAppId(null);
  };

  const addAppToManager = () => {
    if (!appManagerSelectedAppId) return;
    const selectedApp = appManagerApplications.find((app) => app.id === appManagerSelectedAppId);
    if (!selectedApp) return;

    const exists = appManagerApps.some((item) => item.packageName === selectedApp.packageName);
    if (exists) {
      message.warning('该应用已在列表中');
      return;
    }

    const latestVersion = getLatestVersion(selectedApp);
    const newEntry = {
      applicationName: selectedApp.applicationName,
      packageName: selectedApp.packageName,
      versionCode: latestVersion?.versionCode || '',
      installUrl: latestVersion?.installUrl || '',
      applicationIcon: latestVersion?.icon || '',
      allowedMinutesPerDay: '14400',
      installOtherAppsAllowed: 'nocontrol',
      installAllowed: true,
      autoInstall: true,
      upgradeAllowed: true,
      uninstallAllowed: false,
      autoGrantPermissions: true,
      autoUpgrade: true,
      shareDisabled: false
    };

    setAppManagerApps((prev) => [newEntry, ...prev]);

    if (appManagerWhitelistApps) {
      setAppManagerWhitelistApps((prev) => {
        const next = prev ? [...prev] : [];
        if (!next.includes(selectedApp.packageName)) {
          next.push(selectedApp.packageName);
        }
        return next;
      });
    }
  };

  const removeAppFromManager = (index: number) => {
    const removed = appManagerApps[index];
    const nextApps = appManagerApps.filter((_, i) => i !== index);
    setAppManagerApps(nextApps);

    if (appManagerWhitelistApps && removed?.packageName) {
      setAppManagerWhitelistApps((prev) => (prev || []).filter((pkg) => pkg !== removed.packageName));
    }
  };

  const parseNetworkApplyTo = (applyTo?: string) => {
    if (!applyTo || !applyTo.trim()) return ['all'];
    const parts = applyTo.split(',').map((item) => item.trim()).filter(Boolean);
    if (parts.includes('all')) return ['all'];
    return parts.length > 0 ? parts : ['all'];
  };

  const buildNetworkApplyTo = (values: string[]) => {
    if (!values || values.length === 0 || values.includes('all')) return 'all';
    return values.filter((v) => v !== 'all').join(',');
  };

  const normalizeNetworkConfigs = (configs: any[]) => {
    if (!Array.isArray(configs)) return [];
    return configs.map((config) => {
      const rules = Array.isArray(config?.rules) ? config.rules : [];
      const blacklistRule = rules.find((rule: any) => rule?.type === 'blacklist');
      const whitelistRule = rules.find((rule: any) => rule?.type === 'whitelist');
      return {
        applyTo: parseNetworkApplyTo(config?.applyTo),
        blacklist: Array.isArray(blacklistRule?.sites) ? [...blacklistRule.sites] : [],
        whitelist: Array.isArray(whitelistRule?.sites) ? [...whitelistRule.sites] : [],
        blacklistInput: '',
        whitelistInput: ''
      };
    });
  };

  const openNetworkManager = async (policy: Policy) => {
    setNetworkManagerVisible(true);
    setNetworkManagerLoading(true);
    setNetworkManagerPolicy(policy);

    try {
      const data = parsePolicyValue(policy.value);
      setNetworkManagerData(data);

      const policyItems = data?.result?.policies || [];
      const selectedIndex = policyItems.findIndex((item: any) => item?.strategies?.network);
      if (selectedIndex === -1) {
        setNetworkManagerPolicyIndex(null);
        setNetworkManagerNetworkId(null);
        setNetworkManagerStrategyName('');
        setNetworkManagerConfigs([]);
      } else {
        const selectedPolicyItem = policyItems[selectedIndex];
        const networkId = selectedPolicyItem?.strategies?.network || '';
        const networkStrategy = (data?.result?.strategies?.network || []).find((item: any) => item.id === networkId);
        setNetworkManagerPolicyIndex(selectedIndex);
        setNetworkManagerNetworkId(networkId || null);
        setNetworkManagerStrategyName(networkStrategy?.name || '');
        setNetworkManagerConfigs(normalizeNetworkConfigs(networkStrategy?.config || []));
      }

      const apps = await applicationAPI.getApplications();
      setNetworkManagerApplications(apps);
    } catch (err) {
      console.error('加载网址管理数据失败:', err);
      message.error('加载网址管理数据失败');
    } finally {
      setNetworkManagerLoading(false);
    }
  };

  const closeNetworkManager = () => {
    setNetworkManagerVisible(false);
    setNetworkManagerPolicy(null);
    setNetworkManagerData(null);
    setNetworkManagerStrategyName('');
    setNetworkManagerPolicyIndex(null);
    setNetworkManagerNetworkId(null);
    setNetworkManagerConfigs([]);
  };

  const handleSaveNetworkManager = async () => {
    if (!networkManagerPolicy || !networkManagerData || networkManagerPolicyIndex === null || !networkManagerNetworkId) return;

    try {
      setNetworkManagerSaving(true);
      const nextData = JSON.parse(JSON.stringify(networkManagerData));
      const networkList = nextData?.result?.strategies?.network || [];
      const targetNetwork = networkList.find((item: any) => item.id === networkManagerNetworkId);

      if (targetNetwork) {
        targetNetwork.config = networkManagerConfigs.map((config: any) => {
          const blacklistSites = (config.blacklist || []).map((site: string) => site.trim()).filter(Boolean);
          const whitelistSites = (config.whitelist || []).map((site: string) => site.trim()).filter(Boolean);
          const rules = [] as any[];
          rules.push({ type: 'blacklist', sites: blacklistSites });
          rules.push({ type: 'whitelist', sites: whitelistSites });
          return {
            applyTo: buildNetworkApplyTo(config.applyTo || []),
            rules
          };
        });
      }

      await policyAPI.updatePolicy({
        id: networkManagerPolicy.id,
        value: stringifyPolicyValue(nextData)
      });

      message.success('网络访问策略已更新');
      closeNetworkManager();
      await fetchPolicies();
    } catch (err: any) {
      message.error(err.message || '保存失败');
    } finally {
      setNetworkManagerSaving(false);
    }
  };

  const handleManagerVersionSelect = (index: number, option: any) => {
    const next = [...appManagerApps];
    next[index] = {
      ...next[index],
      versionCode: option.versionCode,
      installUrl: option.installUrl,
      applicationIcon: option.icon || ''
    };
    setAppManagerApps(next);
  };

  const handleSaveAppManager = async () => {
    if (!appManagerPolicy || !appManagerData || appManagerPolicyIndex === null || !appManagerAppControlId) return;

    try {
      setAppManagerSaving(true);
      const nextData = JSON.parse(JSON.stringify(appManagerData));
      const appControlList = nextData?.result?.strategies?.appControl || [];
      const targetAppControl = appControlList.find((item: any) => item.id === appManagerAppControlId);

      if (targetAppControl) {
        targetAppControl.apps = appManagerApps;
      }

      if (appManagerWhitelistApps && appManagerAppAllowanceId) {
        const appAllowanceList = nextData?.result?.strategies?.appAllowance || [];
        const targetAppAllowance = appAllowanceList.find((item: any) => item.id === appManagerAppAllowanceId);
        if (targetAppAllowance && targetAppAllowance.controlType === 'whitelist') {
          targetAppAllowance.apps = Array.from(new Set(appManagerWhitelistApps));
        }
      }

      await policyAPI.updatePolicy({
        id: appManagerPolicy.id,
        value: stringifyPolicyValue(nextData)
      });

      message.success('应用控制策略已更新');
      closeAppManager();
      await fetchPolicies();
    } catch (err: any) {
      message.error(err.message || '保存失败');
    } finally {
      setAppManagerSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await policyAPI.deletePolicy(id);
      message.success('管控措施删除成功');
      await fetchPolicies();
    } catch (err: any) {
      message.error(err.message || '删除失败');
      console.error('删除失败:', err);
    }
  };

  // 查看策略使用情况
  const handleViewUsage = async (policy: Policy) => {
    setCurrentPolicyName(policy.name);
    setUsageModalVisible(true);
    setUsageLoading(true);
    setCurrentUsage(null);
    
    try {
      const usage = await policyAPI.getPolicyUsage(policy.id);
      setCurrentUsage(usage);
    } catch (err: any) {
      message.error('获取使用情况失败');
      console.error('获取使用情况失败:', err);
    } finally {
      setUsageLoading(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a: Policy, b: Policy) => a.id - b.id,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      sorter: (a: Policy, b: Policy) => a.name.localeCompare(b.name),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
      sorter: (a: Policy, b: Policy) => {
        const descA = a.description || '';
        const descB = b.description || '';
        return descA.localeCompare(descB);
      },
    },
    ...(!isMobile ? [{
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: Policy, b: Policy) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    }] : []),
    {
      title: '使用状态',
      key: 'usage',
      width: isMobile ? 80 : 100,
      render: (_: any, record: any) => {
        const hasUsage = record.usageCount && (record.usageCount.categories > 0 || record.usageCount.devices > 0);
        if (!hasUsage) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return (
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => handleViewUsage(record)}
            style={{ padding: 0 }}
          >
            {isMobile ? '' : '查看'}
          </Button>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: isMobile ? 100 : 150,
      fixed: 'right' as const,
      render: (_: any, record: Policy) => {
        // 移动端直接显示图标按钮
        if (isMobile) {
          return (
            <Space size={4}>
              <Button
                type="text"
                icon={<AppstoreOutlined />}
                onClick={() => openAppManager(record)}
                size="small"
              />
              <Button
                type="text"
                icon={<GlobalOutlined />}
                onClick={() => openNetworkManager(record)}
                size="small"
              />
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => openCloneModal(record)}
                size="small"
              />
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => showModal(record)}
                size="small"
              />
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                size="small"
                onClick={() => {
                  Modal.confirm({
                    title: '确定要删除这个管控措施吗？',
                    onOk: () => handleDelete(record.id),
                    okText: '确定',
                    cancelText: '取消'
                  });
                }}
              />
            </Space>
          );
        }
        return (
          <Space>
            <Button
              type="link"
              icon={<AppstoreOutlined />}
              onClick={() => openAppManager(record)}
              size="small"
            >
              应用管理
            </Button>
            <Button
              type="link"
              icon={<GlobalOutlined />}
              onClick={() => openNetworkManager(record)}
              size="small"
            >
              网址管理
            </Button>
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => openCloneModal(record)}
              size="small"
            >
              克隆
            </Button>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              size="small"
            >
              编辑
            </Button>
            <Popconfirm
              title="确定要删除这个管控措施吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
                size="small"
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          🛡️ 管控措施列表
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: isMobile ? 12 : 14 }}>
          管理设备管控策略和配置
        </p>
      </div>

      <Card
        className="content-card"
        extra={
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            {isMobile && (
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowSearchForm(!showSearchForm)}
                type={showSearchForm ? 'primary' : 'default'}
              >
                筛选
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchPolicies}
              loading={loading}
            >
              {isMobile ? '' : '刷新'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              {isMobile ? '新增' : '新增管控措施'}
            </Button>
          </Space>
        }
      >
        {/* 搜索表单 */}
        {(showSearchForm || !isMobile) && (
        <Card
          size="small"
          title={isMobile ? '筛选条件' : '搜索条件'}
          style={{ marginBottom: 16 }}
          styles={{ body: { paddingBottom: 0 } }}
        >
          <Form
            form={searchForm}
            onFinish={handleSearch}
            layout={isMobile ? 'vertical' : 'inline'}
            colon={false}
            className={isMobile ? 'search-form-mobile' : ''}
          >
            <Row gutter={[12, 0]} style={{ width: '100%' }}>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="id"
                  label="ID"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="输入ID"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="name"
                  label="名称"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="输入名称关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="description"
                  label="描述"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="输入描述关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="value"
                  label="配置值"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="输入配置值关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 0]} style={{ width: '100%' }}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="dateRange"
                  label="创建时间"
                  style={{ marginBottom: 8 }}
                >
                  <RangePicker
                    style={{ width: '100%' }}
                    placeholder={['开始日期', '结束日期']}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={16}>
                <Form.Item label={isMobile ? undefined : ' '} style={{ marginBottom: 8 }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      htmlType="submit"
                    >
                      搜索
                    </Button>
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleResetSearch}
                    >
                      重置
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
        )}
        <div className="mobile-table-wrapper">
        <Table
          columns={columns}
          dataSource={filteredPolicies}
          rowKey="id"
          loading={loading}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: isMobile ? 400 : undefined }}
          pagination={{
            defaultPageSize: isMobile ? 20 : 100,
            pageSizeOptions: isMobile ? ['20', '50', '100'] : ['50', '100', '200', '500'],
            showSizeChanger: true,
            showQuickJumper: !isMobile,
            showTotal: (total, range) => isMobile ? `${total}条` : `第 ${range?.[0]}-${range?.[1]} 条，共 ${total} 条记录`,
            size: isMobile ? 'small' : 'default'
          }}
          className="policy-table"
        />
        </div>
      </Card>

      <Modal
        title={editingPolicy ? '编辑管控措施' : '新增管控措施'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? '95%' : 1200}
        style={{ top: isMobile ? 10 : 20 }}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            name="name"
            label="名称"
            rules={[
              { required: true, message: '请输入管控措施名称' },
              { max: 100, message: '名称长度不能超过100个字符' }
            ]}
          >
            <Input placeholder="请输入管控措施名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[
              { max: 500, message: '描述长度不能超过500个字符' }
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入管控措施描述"
            />
          </Form.Item>

          <Form.Item label="配置值">
            <Tabs 
              activeKey={editMode} 
              onChange={(key) => {
                setEditMode(key as 'visual' | 'json');
                // 切换到 JSON 模式时，同步数据到表单
                if (key === 'json' && strategyData) {
                  form.setFieldsValue({
                    value: JSON.stringify(normalizePolicyData(strategyData), null, 2)
                  });
                }
                // 切换到可视化模式时，尝试解析 JSON
                if (key === 'visual') {
                  const jsonValue = form.getFieldValue('value');
                  if (jsonValue) {
                    try {
                      const parsed = JSON5.parse(jsonValue);
                      setStrategyData(normalizePolicyData(parsed));
                    } catch (e) {
                      message.warning('JSON 格式有误，无法切换到可视化编辑');
                      setEditMode('json');
                    }
                  }
                }
              }}
            >
              <Tabs.TabPane tab="可视化编辑" key="visual">
                {strategyData && strategyData.result && (
                  <div style={{ 
                    border: '1px solid #d9d9d9', 
                    borderRadius: 4, 
                    padding: 16,
                    maxHeight: '60vh',
                    overflowY: 'auto'
                  }}>
                    <ConfigProvider componentSize="small">
                      <StrategyVisualEditor
                        data={strategyData.result}
                        onChange={(newResultData) => {
                          // 更新时保持包装结构
                          setStrategyData({
                            ...strategyData,
                            result: newResultData
                          });
                        }}
                        generateGuid={generateGuid}
                      />
                    </ConfigProvider>
                  </div>
                )}
              </Tabs.TabPane>
              
              <Tabs.TabPane tab="JSON 编辑" key="json">
                <Form.Item
                  name="value"
                  rules={[
                    { required: editMode === 'json', message: '请输入配置值' },
                    {
                      validator: async (_: any, value: string) => {
                        if (editMode !== 'json') return Promise.resolve();
                        if (!value || !value.trim()) return Promise.resolve();
                        try {
                          JSON5.parse(value);
                          return Promise.resolve();
                        } catch (e) {
                          return Promise.reject(new Error('配置值不是有效的 JSON/JSON5'));
                        }
                      }
                    }
                  ]}
                  noStyle
                >
                  <TextArea
                    rows={15}
                    placeholder="请输入配置值，支持 JSON/JSON5，保存时会标准化为严格 JSON"
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </Form.Item>
              </Tabs.TabPane>
            </Tabs>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                {editingPolicy ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`克隆管控措施${clonePolicy ? ` - ${clonePolicy.name}` : ''}`}
        open={cloneModalVisible}
        onCancel={handleCloneCancel}
        okText="克隆"
        cancelText="取消"
        onOk={() => cloneForm.submit()}
        confirmLoading={cloneSubmitting}
        width={isMobile ? '95%' : 500}
      >
        <Form
          form={cloneForm}
          onFinish={handleCloneSubmit}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            name="name"
            label="新名称"
            rules={[
              { required: true, message: '请输入新名称' },
              { max: 100, message: '名称长度不能超过100个字符' }
            ]}
          >
            <Input placeholder="请输入克隆后的名称" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 使用情况弹框 */}
      <Modal
        title={`管控措施「${currentPolicyName}」使用情况`}
        open={usageModalVisible}
        onCancel={() => setUsageModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setUsageModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={isMobile ? '95%' : 500}
      >
        {usageLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin tip="加载中..." />
          </div>
        ) : currentUsage ? (
          <div>
            {currentUsage.categories.length === 0 && currentUsage.devices.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                此管控措施未被任何目录或设备使用
              </div>
            ) : (
              <>
                {currentUsage.categories.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Typography.Text strong>引用的目录节点 ({currentUsage.categories.length})</Typography.Text>
                    <List
                      size="small"
                      bordered
                      dataSource={currentUsage.categories}
                      renderItem={(item) => (
                        <List.Item>
                          <span><FolderOutlined style={{ marginRight: 8, color: '#faad14' }} />{item.name}</span>
                          <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                            (ID: {item.id})
                          </Typography.Text>
                        </List.Item>
                      )}
                      style={{ marginTop: 8, maxHeight: 200, overflow: 'auto' }}
                    />
                  </div>
                )}
                {currentUsage.devices.length > 0 && (
                  <div>
                    <Typography.Text strong>引用的设备 ({currentUsage.devices.length})</Typography.Text>
                    <List
                      size="small"
                      bordered
                      dataSource={currentUsage.devices}
                      renderItem={(item) => (
                        <List.Item>
                          <span><MobileOutlined style={{ marginRight: 8, color: '#1890ff' }} />{item.deviceCode}</span>
                          {item.description && (
                            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                              ({item.description})
                            </Typography.Text>
                          )}
                        </List.Item>
                      )}
                      style={{ marginTop: 8, maxHeight: 200, overflow: 'auto' }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        ) : null}
      </Modal>

      {/* 应用管理弹窗 */}
      <Modal
        title={
          <Space>
            <AppstoreOutlined />
            <span>
              应用管理{appManagerPolicy ? ` - ${appManagerPolicy.name}` : ''}
            </span>
          </Space>
        }
        open={appManagerVisible}
        onCancel={closeAppManager}
        footer={[
          <Button key="cancel" onClick={closeAppManager}>
            取消
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveAppManager}
            loading={appManagerSaving}
            disabled={!appManagerAppControlId || appManagerPolicyIndex === null}
          >
            保存
          </Button>
        ]}
        width={isMobile ? '95%' : 900}
        style={{ top: isMobile ? 10 : 20 }}
      >
        {appManagerLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin tip="加载中..." />
          </div>
        ) : !appManagerAppControlId ? (
          <Empty description="未选择应用控制策略，请联系管理员配置应用策略" />
        ) : (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">当前应用控制策略：</Text>
              <Text strong>{appManagerStrategyName || '未命名策略'}</Text>
            </div>

            <Card size="small" title="添加应用" style={{ marginBottom: 16 }}>
              {appManagerApplications.length === 0 ? (
                <Empty description="请先在应用管理中上传应用" />
              ) : (
                <Row gutter={12} align="middle">
                  <Col xs={24} sm={16} md={18}>
                    <Select
                      showSearch
                      placeholder="选择要添加的应用"
                      optionFilterProp="label"
                      style={{ width: '100%' }}
                      value={appManagerSelectedAppId || undefined}
                      onChange={(value) => setAppManagerSelectedAppId(value)}
                      options={appManagerApplications.map((app) => {
                        const disabled = appManagerApps.some((item) => item.packageName === app.packageName);
                        return {
                          label: `${app.applicationName} (${app.packageName})${disabled ? ' - 已添加' : ''}`,
                          value: app.id,
                          disabled
                        };
                      })}
                      filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) || false
                      }
                    />
                  </Col>
                  <Col xs={24} sm={8} md={6}>
                    <Button type="primary" block onClick={addAppToManager} disabled={!appManagerSelectedAppId}>
                      添加
                    </Button>
                  </Col>
                </Row>
              )}
            </Card>

            <Card size="small" title="已选应用" style={{ marginBottom: 16 }}>
              <div style={{ maxHeight: isMobile ? '60vh' : '65vh', overflowY: 'auto', paddingRight: 4 }}>
                {appManagerApps.length === 0 ? (
                  <Empty description="暂无应用" />
                ) : (
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {appManagerApps.map((app, index) => {
                      const matchedApp = findApplicationForEntry(app);
                      const versions = matchedApp ? getSortedVersions(matchedApp.versions) : [];
                      return (
                        <Card key={`${app.packageName || app.applicationName || index}`} size="small" style={{ background: '#fafafa' }}>
                          <Row gutter={[16, 12]} align="middle">
                            <Col flex="40px">
                              <div style={{ width: 32, height: 32, borderRadius: 6, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {getAppIcon(app) ? (
                                  <Image src={getAppIcon(app)} width={28} height={28} preview={false} style={{ borderRadius: 6 }} />
                                ) : (
                                  <AppstoreOutlined style={{ color: '#999' }} />
                                )}
                              </div>
                            </Col>
                            <Col flex="auto">
                              <div style={{ fontWeight: 500 }}>{app.applicationName || '未命名应用'}</div>
                              <Text type="secondary" style={{ fontSize: 12 }}>{app.packageName || '-'}</Text>
                            </Col>
                            <Col xs={24} sm={10} md={8}>
                              <AutoComplete
                                value={app.versionCode || ''}
                                placeholder="选择或输入版本"
                                options={versions.map((v) => ({
                                  value: `${v.versionCode}||${v.installUrl || ''}`,
                                  versionCode: v.versionCode,
                                  installUrl: v.installUrl,
                                  icon: v.icon,
                                  label: v.versionCode
                                }))}
                                onChange={(value) => handleManagerVersionChange(index, value)}
                                onSelect={(_, option: any) => handleManagerVersionSelect(index, option)}
                                style={{ width: '100%' }}
                              />
                            </Col>
                            <Col xs={24} sm={4} md={3} style={{ textAlign: 'right' }}>
                              <Button danger onClick={() => removeAppFromManager(index)}>
                                删除
                              </Button>
                            </Col>
                          </Row>
                        </Card>
                      );
                    })}
                  </Space>
                )}
              </div>
            </Card>
          </div>
        )}
      </Modal>

      {/* 网址管理弹窗 */}
      <Modal
        title={
          <Space>
            <GlobalOutlined />
            <span>
              网址管理{networkManagerPolicy ? ` - ${networkManagerPolicy.name}` : ''}
            </span>
          </Space>
        }
        open={networkManagerVisible}
        onCancel={closeNetworkManager}
        footer={[
          <Button key="cancel" onClick={closeNetworkManager}>
            取消
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveNetworkManager}
            loading={networkManagerSaving}
            disabled={!networkManagerNetworkId || networkManagerPolicyIndex === null}
          >
            保存
          </Button>
        ]}
        width={isMobile ? '95%' : 900}
        style={{ top: isMobile ? 10 : 20 }}
      >
        {networkManagerLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin tip="加载中..." />
          </div>
        ) : !networkManagerNetworkId ? (
          <Empty description="未选择网络访问策略，请联系管理员配置网络策略" />
        ) : (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">当前网络访问策略：</Text>
              <Text strong>{networkManagerStrategyName || '未命名策略'}</Text>
            </div>

            <Alert
              type="error"
              description={
                <div>
                  <div>www.baidu.com 表示禁用百度这一个域名；tieba.baidu.com不会被禁用</div>
                  <div>*.baidu.com 表示禁用百度下所有子域名</div>
                  <div>禁用 IP 示例：211.103.#.#（# 代表任意段）</div>
                  <div>*.* 表示所有网址均被禁用，只有白名单可以访问</div>
                  <div>白名单规则与黑名单含义相反</div>
                  <Text strong>如果同一网址同时在白名单和黑名单中，白名单优先生效</Text>
                </div>
              }
            />

            {networkManagerConfigs.length === 0 ? (
              <Empty description="暂无规则" />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {networkManagerConfigs.map((config: any, configIndex: number) => (
                  <Card
                    key={`network-rule-${configIndex}`}
                    size="small"
                    title={`规则 ${configIndex + 1}`}
                    extra={
                      <Button
                        danger
                        size="small"
                        onClick={() => {
                          const next = networkManagerConfigs.filter((_: any, idx: number) => idx !== configIndex);
                          setNetworkManagerConfigs(next);
                        }}
                      >
                        删除
                      </Button>
                    }
                  >
                    <div style={{ marginBottom: 12 }}>
                      <label>策略运用到（所有应用/指定应用）：</label>
                      <Select
                        mode="multiple"
                        style={{ width: '100%', marginTop: 6 }}
                        value={config.applyTo}
                        placeholder="选择应用（可多选）"
                        optionFilterProp="label"
                        onSelect={(value) => {
                          if (value === 'all') {
                            const next = [...networkManagerConfigs];
                            next[configIndex] = { ...next[configIndex], applyTo: ['all'] };
                            setNetworkManagerConfigs(next);
                            return;
                          }
                          const currentValues = Array.isArray(config.applyTo) ? config.applyTo : [];
                          const nextValues = Array.from(new Set(currentValues.filter((v: string) => v !== 'all').concat(value as string)));
                          const next = [...networkManagerConfigs];
                          next[configIndex] = { ...next[configIndex], applyTo: nextValues };
                          setNetworkManagerConfigs(next);
                        }}
                        onDeselect={(value) => {
                          const currentValues = Array.isArray(config.applyTo) ? config.applyTo : [];
                          const nextValues = currentValues.filter((v: string) => v !== value);
                          const next = [...networkManagerConfigs];
                          next[configIndex] = { ...next[configIndex], applyTo: nextValues };
                          setNetworkManagerConfigs(next);
                        }}
                        options={[
                          { label: '所有应用', value: 'all' },
                          ...networkManagerApplications.map((app) => ({
                            label: `${app.applicationName} (${app.packageName})`,
                            value: app.packageName
                          }))
                        ]}
                        filterOption={(input, option) =>
                          (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) || false
                        }
                      />
                    </div>

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <div style={{ fontWeight: 500, marginBottom: 8 }}>黑名单</div>
                        <Row gutter={8} style={{ marginBottom: 8 }}>
                          <Col flex="auto">
                            <Input
                              value={config.blacklistInput}
                              placeholder="输入网址或IP"
                              onChange={(e) => {
                                const next = [...networkManagerConfigs];
                                next[configIndex] = { ...next[configIndex], blacklistInput: e.target.value };
                                setNetworkManagerConfigs(next);
                              }}
                            />
                          </Col>
                          <Col>
                            <Button
                              type="primary"
                              onClick={() => {
                                const value = (config.blacklistInput || '').trim();
                                if (!value) return;
                                const next = [...networkManagerConfigs];
                                const list = Array.from(new Set([...(next[configIndex].blacklist || []), value]));
                                next[configIndex] = { ...next[configIndex], blacklist: list, blacklistInput: '' };
                                setNetworkManagerConfigs(next);
                              }}
                            >
                              添加
                            </Button>
                          </Col>
                        </Row>
                        {(config.blacklist || []).length === 0 ? (
                          <Empty description="暂无黑名单" />
                        ) : (
                          <div style={{ maxHeight: 180, overflowY: 'auto', overflowX: 'hidden' }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              {(config.blacklist || []).map((site: string, siteIndex: number) => (
                                <Row key={`black-${siteIndex}`} gutter={8} align="middle">
                                  <Col flex="auto">
                                    <Text style={{ wordBreak: 'break-all' }}>{site}</Text>
                                  </Col>
                                  <Col>
                                    <Button
                                      danger
                                      size="small"
                                      onClick={() => {
                                        const next = [...networkManagerConfigs];
                                        const nextList = (next[configIndex].blacklist || []).filter((_: any, idx: number) => idx !== siteIndex);
                                        next[configIndex] = { ...next[configIndex], blacklist: nextList };
                                        setNetworkManagerConfigs(next);
                                      }}
                                    >
                                      移除
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                            </Space>
                          </div>
                        )}
                      </Col>

                      <Col xs={24} md={12}>
                        <div style={{ fontWeight: 500, marginBottom: 8 }}>白名单</div>
                        <Row gutter={8} style={{ marginBottom: 8 }}>
                          <Col flex="auto">
                            <Input
                              value={config.whitelistInput}
                              placeholder="输入网址或IP"
                              onChange={(e) => {
                                const next = [...networkManagerConfigs];
                                next[configIndex] = { ...next[configIndex], whitelistInput: e.target.value };
                                setNetworkManagerConfigs(next);
                              }}
                            />
                          </Col>
                          <Col>
                            <Button
                              type="primary"
                              onClick={() => {
                                const value = (config.whitelistInput || '').trim();
                                if (!value) return;
                                const next = [...networkManagerConfigs];
                                const list = Array.from(new Set([...(next[configIndex].whitelist || []), value]));
                                next[configIndex] = { ...next[configIndex], whitelist: list, whitelistInput: '' };
                                setNetworkManagerConfigs(next);
                              }}
                            >
                              添加
                            </Button>
                          </Col>
                        </Row>
                        {(config.whitelist || []).length === 0 ? (
                          <Empty description="暂无白名单" />
                        ) : (
                          <div style={{ maxHeight: 180, overflowY: 'auto', overflowX: 'hidden' }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              {(config.whitelist || []).map((site: string, siteIndex: number) => (
                                <Row key={`white-${siteIndex}`} gutter={8} align="middle">
                                  <Col flex="auto">
                                    <Text style={{ wordBreak: 'break-all' }}>{site}</Text>
                                  </Col>
                                  <Col>
                                    <Button
                                      danger
                                      size="small"
                                      onClick={() => {
                                        const next = [...networkManagerConfigs];
                                        const nextList = (next[configIndex].whitelist || []).filter((_: any, idx: number) => idx !== siteIndex);
                                        next[configIndex] = { ...next[configIndex], whitelist: nextList };
                                        setNetworkManagerConfigs(next);
                                      }}
                                    >
                                      移除
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                            </Space>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            )}

            <Button
              type="dashed"
              onClick={() => {
                setNetworkManagerConfigs((prev) => [
                  ...prev,
                  {
                    applyTo: ['all'],
                    blacklist: [],
                    whitelist: [],
                    blacklistInput: '',
                    whitelistInput: ''
                  }
                ]);
              }}
              style={{ marginTop: 12, width: '100%' }}
            >
              添加规则
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Policies;
